(() => {
  'use strict';

  const testHook = globalThis.__AVID_TITANIUM_CENTER_TEST_HOOK__;

  const clean = (value) => String(value ?? '').trim();
  const token = (value) => clean(value).toLowerCase().replace(/[_\s]+/g, '-');
  const label = (value) => clean(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  const customerLabel = (value) => label(value).replace(/^Sae$/, 'SAE');
  const formatCents = (value) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format((Number(value) || 0) / 100);
  const numeric = (value) => {
    const match = clean(value).replace(',', '.').match(/-?\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
  };
  const responsiveSrcset = (source, widths) => {
    try {
      const url = new URL(source, globalThis.location?.origin || 'https://avidrace.com');
      if (!url.pathname.includes('/cdn/shop/')) return '';
      return widths.map((width) => {
        const candidate = new URL(url);
        candidate.searchParams.set('width', String(width));
        return `${candidate.toString()} ${width}w`;
      }).join(', ');
    } catch (_error) {
      return '';
    }
  };

  const toList = (value) => {
    if (Array.isArray(value)) return value.flatMap(toList).filter(Boolean);
    if (value && typeof value === 'object') {
      if ('value' in value) return toList(value.value);
      return Object.values(value).flatMap(toList).filter(Boolean);
    }
    const entry = clean(value);
    if (!entry) return [];
    return entry.split(/\s*[|;]\s*/).map(clean).filter(Boolean);
  };

  const unique = (values) => [...new Set(toList(values).map(clean).filter(Boolean))];

  const productIdentity = (product, index = 0) => clean(product?.id || product?.handle || product?.url) || `catalog-entry-${index}`;
  const uniqueCatalogProducts = (entries) => [...new Map(entries.map((product, index) => [productIdentity(product, index), product])).values()];
  const isCatalogReconciled = (entries, loadedPageCount, totalPageCount, expectedSourceCount) => {
    const expected = Number(expectedSourceCount);
    return Number.isFinite(expected)
      && expected >= 0
      && loadedPageCount >= totalPageCount
      && uniqueCatalogProducts(entries).length === expected;
  };

  const withUnit = (value, unit) => toList(value).map((entry) => {
    const normalized = clean(entry);
    if (!normalized) return '';
    return new RegExp(`\\b${unit.replace('/', '\\/')}\\b`, 'i').test(normalized) ? normalized : `${normalized} ${unit}`;
  }).filter(Boolean);

  const greatestCommonDivisor = (left, right) => {
    let a = Math.abs(left);
    let b = Math.abs(right);
    while (b) [a, b] = [b, a % b];
    return a || 1;
  };

  const inchFraction = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) return '';
    const whole = Math.floor(amount);
    const numerator = Math.round((amount - whole) * 64);
    if (numerator === 0) return `${whole} in`;
    if (numerator === 64) return `${whole + 1} in`;
    const divisor = greatestCommonDivisor(numerator, 64);
    const fraction = `${numerator / divisor}/${64 / divisor}`;
    return `${whole ? `${whole} ` : ''}${fraction} in`;
  };

  const measurementValues = (product, labelKey, metricValue, inchValue) => {
    const labels = product?.measurementLabels && typeof product.measurementLabels === 'object'
      ? toList(product.measurementLabels[labelKey])
      : [];
    if (labels.length) return labels.map(normalizeCatalogMeasure);
    return unique([withUnit(metricValue, 'mm'), ...toList(inchValue).map(inchFraction)]).map(normalizeCatalogMeasure);
  };

  const normalizeHardwareClass = (value) => {
    const source = token(value);
    if (source.includes('washer')) return 'Washer';
    if (source.includes('nut')) return 'Nut';
    if (source.includes('stud')) return 'Stud';
    if (source.includes('bolt') || source.includes('screw') || /^(?:button|cap|flat|pan)-head$/.test(source)) return 'Bolt';
    if (source.includes('spacer') || source.includes('misalignment') || source.includes('reducer')) return 'Spacer';
    if (source.includes('clevis')) return 'Clevis';
    if (source.includes('pin')) return 'Pin';
    return customerLabel(value);
  };

  const normalizeDriveStyle = (value) => {
    const source = clean(value);
    if (!source) return '';
    if (/^(?:12|twelve)[-_ ]*point(?:[-_ ]+flange)?$/i.test(source)) return '12-Point';
    if (/^(?:6|six)[-_ ]*point$/i.test(source)) return '6-Point';
    if (/^T\d+$/i.test(source) || /torx/i.test(source)) return 'Torx';
    if (/allen|hex socket/i.test(source)) return 'Allen';
    return normalizeCatalogStyle(source);
  };

  const canonicalStandard = (value) => {
    const normalized = token(value);
    if (/^(metric|mm|iso|din)$/.test(normalized)) return 'metric';
    if (/^(sae|inch|imperial|unc|unf|unef)$/.test(normalized)) return 'sae';
    return normalized;
  };

  const inferStandard = (text) => {
    const source = clean(text);
    if (/\b(metric|iso|din)\b|\bM\d+(?:\.\d+)?\b/i.test(source)) return 'metric';
    if (/\b(sae|inch|imperial|unc|unf|unef)\b|\b\d+\s*\/\s*\d+(?:\s*[-x]\s*\d+)?\b/i.test(source)) return 'sae';
    return '';
  };

  const inferHardwareType = (text) => {
    const source = clean(text).toLowerCase().replace(/[_-]+/g, ' ');
    const matches = [
      ['bolt', /\bbolt(s)?\b/],
      ['nut', /\bnut(s)?\b/],
      ['washer', /\bwasher(s)?\b/],
      ['stud', /\bstud(s)?\b/],
      ['screw', /\bscrew(s)?\b/],
      ['spacer', /\bspacer(s)?\b|misalignment/],
      ['clevis', /\bclevis\b/],
      ['pin', /\bpin(s)?\b/]
    ];
    return matches.find(([, pattern]) => pattern.test(source))?.[0] || '';
  };

  const normalizeCatalogMeasure = (value) => {
    const source = clean(value);
    if (!source) return '';
    const metric = source.match(/^((?:\d+(?:\.\d+)?)|(?:\.\d+))\s*mm$/i);
    if (metric) return `${Number(metric[1])} mm`;
    const inch = source.match(/^((?:\d+(?:\.\d+)?)|(?:\.\d+)|(?:\d+\s*\/\s*\d+))\s*(?:in|inch|inches|")$/i);
    if (inch) {
      const amount = inch[1].includes('/') ? inch[1].replace(/\s+/g, '') : String(Number(inch[1]));
      return `${amount} in`;
    }
    return source;
  };

  const normalizePublishedMeasure = (value) => {
    const normalized = normalizeCatalogMeasure(value);
    return /^-?(?:(?:\d+(?:\.\d+)?)|(?:\.\d+)|(?:\d+\/\d+))\s+(?:mm|in)$/i.test(normalized)
      ? normalized
      : '';
  };

  const displayFacetValue = (value) => {
    const normalized = normalizeCatalogMeasure(value);
    if (/^(?:6|12)-Point$/i.test(normalized)) return normalized;
    if (/\b(?:mm|in|TPI)$/i.test(normalized)) {
      return normalized
        .replace(/\bmm$/i, 'mm')
        .replace(/\bin$/i, 'in')
        .replace(/\bTPI$/i, 'TPI');
    }
    return customerLabel(normalized);
  };

  const catalogThreadFromMetafields = (product) => {
    const source = clean(product.threadDesignation);
    const metric = source.match(/\bM(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)(?!\s*(?:mm|in|"))\b/i);
    if (metric) return { diameter: `${Number(metric[1])} mm`, pitch: `${Number(metric[2])} mm` };
    const gauge = source.match(/^#?(\d+)\s*[-x×]\s*(\d{2,3})\b/i);
    if (gauge && Number(gauge[1]) <= 14) return { diameter: `#${Number(gauge[1])}`, pitch: `${Number(gauge[2])} TPI` };
    const collapsedMixed = source.match(/^([1-9])(\d+)\s*\/\s*(\d+)\s*[-x×]\s*(\d{2,3})\b/i);
    if (collapsedMixed) {
      const amount = Number(collapsedMixed[1]) + (Number(collapsedMixed[2]) / Number(collapsedMixed[3]));
      return { diameter: inchFraction(amount), pitch: `${Number(collapsedMixed[4])} TPI` };
    }
    const mixed = source.match(/^([1-9]\d*)\s+(\d+)\s*\/\s*(\d+)\s*[-x×]\s*(\d{2,3})\b/i);
    if (mixed) {
      const amount = Number(mixed[1]) + (Number(mixed[2]) / Number(mixed[3]));
      return { diameter: inchFraction(amount), pitch: `${Number(mixed[4])} TPI` };
    }
    const sae = source.match(/\b(\d+\s*\/\s*\d+)\s*[-x×]\s*(\d{2,3})\b/i);
    if (sae) return { diameter: `${sae[1].replace(/\s+/g, '')} in`, pitch: `${Number(sae[2])} TPI` };
    return { diameter: '', pitch: '' };
  };

  const normalizeCatalogStyle = (value) => customerLabel(value)
    .replace(/^12P\b/i, '12-Point')
    .replace(/^6P\b/i, '6-Point');

  const reviewedStatuses = new Set(['verified', 'complete', 'published']);
  const browseFacetKeys = new Set(['hardwareType']);
  const hasExactFilter = (filters) => Object.entries(filters.facets || {})
    .some(([key, value]) => !browseFacetKeys.has(key) && toList(value).length > 0);

  const normalizeProduct = (product, index = 0) => {
    const sourceText = [product.title, product.productType, product.sku, ...toList(product.tags)].join(' ');
    const declaredStandards = unique(product.measurementSystem).map(canonicalStandard).filter(Boolean);
    const displayStandards = declaredStandards;
    const rawHardwareTypes = unique(product.componentType);
    const hardwareTypes = unique(rawHardwareTypes.map(normalizeHardwareClass));
    const displayHardwareTypes = hardwareTypes;
    const browseHardwareTypes = hardwareTypes;

    const metafieldThread = catalogThreadFromMetafields(product);
    const isGaugeThread = /^#\d+$/.test(metafieldThread.diameter);
    const diameter = unique([measurementValues(product, 'nominalDiameter', product.nominalDiameterMm, isGaugeThread ? null : product.nominalDiameterIn), metafieldThread.diameter]);
    const pitch = unique([product.threadDesignation, withUnit(product.threadPitchMm, 'mm'), withUnit(product.threadsPerInch, 'TPI')]);
    const overallLength = measurementValues(product, 'overallLength', product.overallLengthMm, product.overallLengthIn);
    const threadLength = measurementValues(product, 'threadLength', product.threadLengthMm, product.threadLengthIn);
    const grip = measurementValues(product, 'shankLength', product.shankLengthMm, product.shankLengthIn);
    const wrenchSize = unique([measurementValues(product, 'wrenchSize', product.wrenchSizeMm, product.wrenchSizeIn), product.wrenchSizeDisplay]).map(normalizeCatalogMeasure);
    const flangeDiameter = measurementValues(product, 'flangeDiameter', product.flangeDiameterMm, product.flangeDiameterIn);
    const headDiameter = measurementValues(product, 'headDiameter', product.headDiameterMm, product.headDiameterIn);
    const headHeight = measurementValues(product, 'headHeight', product.headHeightMm, product.headHeightIn);
    const washerId = measurementValues(product, 'washerId', product.washerIdMm, product.washerIdIn);
    const washerOd = measurementValues(product, 'washerOd', product.washerOdMm, product.washerOdIn);
    const washerThickness = measurementValues(product, 'washerThickness', product.washerThicknessMm, product.washerThicknessIn);
    const boltHole = measurementValues(product, 'boltHole', product.boltHoleMm, product.boltHoleIn);
    const stackWidth = measurementValues(product, 'stackWidth', product.stackWidthMm, product.stackWidthIn);
    const finishOptions = (Array.isArray(product.options) ? product.options : [])
      .filter((option) => /(finish|color|anodiz)/i.test(clean(option.name)))
      .flatMap((option) => toList(option.values));
    const dataStatus = token(product.dataStatus || 'unreviewed');
    const browseEligible = product.eligible !== false && product.available !== null;
    const reviewed = reviewedStatuses.has(dataStatus);
    const priceReviewRequired = product.priceReviewRequired === true
      || (product.available === true && Number(product.price) <= 0);
    const hardwareClass = token(hardwareTypes[0]);
    const isWasher = hardwareClass === 'washer';
    const isNut = hardwareClass === 'nut';
    const isThreadedFastener = ['bolt', 'stud'].includes(hardwareClass);
    const measurementsForDeclaredStandard = (values, { allowGauge = false } = {}) => {
      const normalized = unique(values).map(normalizeCatalogMeasure);
      if (declaredStandards.length !== 1) return normalized;
      if (declaredStandards[0] === 'metric') return normalized.filter((value) => /\bmm$/i.test(value));
      if (declaredStandards[0] === 'sae') {
        return normalized.filter((value) => /\bin$/i.test(value) || (allowGauge && /^#\d+$/.test(value)));
      }
      return normalized;
    };
    const browseFacets = {
      hardwareType: browseHardwareTypes,
      diameter: measurementsForDeclaredStandard([isWasher ? washerId : diameter, metafieldThread.diameter], { allowGauge: true }),
      pitch: isWasher ? [] : unique([withUnit(product.threadPitchMm, 'mm'), withUnit(product.threadsPerInch, 'TPI'), metafieldThread.pitch]),
      overallLength: isWasher || isNut ? [] : measurementsForDeclaredStandard(overallLength),
      grip: isThreadedFastener ? measurementsForDeclaredStandard(grip) : [],
      threadLength: isThreadedFastener ? measurementsForDeclaredStandard(threadLength) : [],
      headStyle: isThreadedFastener ? unique(product.headStyle).map(normalizeCatalogStyle) : [],
      driveStyle: isThreadedFastener ? unique(product.driveStyle).map(normalizeDriveStyle) : [],
      wrenchSize: isWasher ? [] : measurementsForDeclaredStandard(wrenchSize),
      materialGrade: unique(product.materialGrade),
      finish: unique([product.finish, finishOptions]),
      quantity: unique([product.packQuantity, product.pieceCount])
    };

    return {
      ...product,
      index,
      standards: declaredStandards,
      displayStandards,
      hardwareTypes,
      hardwareType: hardwareTypes,
      displayHardwareTypes,
      browseHardwareTypes,
      browseFacets,
      diameter,
      pitch,
      overallLength,
      displayPitch: pitch,
      displayOverallLength: overallLength,
      threadLength: threadLength.map(normalizeCatalogMeasure),
      grip: grip.map(normalizeCatalogMeasure),
      wrenchSize,
      flangeDiameter,
      headDiameter,
      headHeight,
      washerId,
      washerOd,
      washerThickness: washerThickness.map(normalizeCatalogMeasure),
      boltHole,
      stackWidth,
      headStyle: unique(product.headStyle),
      driveStyle: unique(product.driveStyle).map(normalizeDriveStyle),
      nutStyle: unique(product.nutStyle),
      lockingStyle: unique(product.lockingStyle),
      threadDirection: unique(product.threadDirection),
      bearingId: withUnit(product.bearingIdIn, 'in'),
      ballRadius: unique(product.ballRadius),
      finish: unique([product.finish, finishOptions]),
      quantity: unique([product.packQuantity, product.pieceCount]),
      materialGrade: unique(product.materialGrade),
      displayMaterialGrade: unique(product.materialGrade),
      materialLabel: 'Grade',
      dataStatus,
      browseEligible,
      reviewed,
      // Ignore legacy billing flags supplied by a cached catalog response.
      anodizingEligible: null,
      priceReviewRequired,
      exactEligible: browseEligible && reviewed,
      searchIndex: [sourceText, diameter, pitch, product.legacyThread, overallLength, product.legacyLength, product.legacyThreadLength, product.legacyShankLength, threadLength, grip, wrenchSize, flangeDiameter, headDiameter, headHeight, washerId, washerOd, washerThickness, boltHole, stackWidth, product.headStyle, product.driveStyle, product.nutStyle, product.lockingStyle, product.materialGrade, product.legacyMaterial, product.finish, product.application]
        .flatMap(toList)
        .join(' ')
        .toLowerCase()
    };
  };

  const facetContains = (values, selected) => {
    const selectedValues = toList(selected).map(token).filter(Boolean);
    return !selectedValues.length || values.some((value) => selectedValues.includes(token(value)));
  };

  const selectedFacetValues = (control) => [...control.selectedOptions]
    .map((option) => clean(option.value))
    .filter(Boolean);

  const matchesFilters = (product, filters) => {
    if (!product.browseEligible) return false;
    if (filters.standard && !product.displayStandards.includes(filters.standard)) return false;
    if (filters.available && !product.available) return false;
    if (filters.search && !product.searchIndex.includes(filters.search.toLowerCase())) return false;
    return Object.entries(filters.facets).every(([key, value]) => {
      const values = product.browseFacets?.[key] || [];
      return facetContains(values || [], value);
    });
  };

  const sortProducts = (products, sort) => {
    const output = [...products];
    const titleSort = (a, b) => clean(a.title).localeCompare(clean(b.title), undefined, { numeric: true });
    if (sort === 'title') return output.sort(titleSort);
    if (sort === 'price-asc') return output.sort((a, b) => Number(a.price || 0) - Number(b.price || 0) || titleSort(a, b));
    if (sort === 'price-desc') return output.sort((a, b) => Number(b.price || 0) - Number(a.price || 0) || titleSort(a, b));
    if (sort === 'diameter') return output.sort((a, b) => numeric(a.browseFacets?.diameter?.[0]) - numeric(b.browseFacets?.diameter?.[0]) || titleSort(a, b));
    if (sort === 'length') return output.sort((a, b) => numeric(a.browseFacets?.overallLength?.[0]) - numeric(b.browseFacets?.overallLength?.[0]) || titleSort(a, b));
    return output.sort((a, b) => a.index - b.index);
  };

  if (testHook) {
    Object.assign(testHook, {
      canonicalStandard,
      customerLabel,
      facetContains,
      hasExactFilter,
      inferHardwareType,
      inferStandard,
      inchFraction,
      matchesFilters,
      normalizeDriveStyle,
      normalizeHardwareClass,
      normalizeProduct,
      displayFacetValue,
      normalizePublishedMeasure,
      isCatalogReconciled,
      sortProducts,
      toList,
      uniqueCatalogProducts
    });
  }

  const readCatalogScript = (script) => {
    try {
      return JSON.parse(script?.textContent || '[]');
    } catch (error) {
      console.warn('AVID Titanium Center catalog data could not be read.', error);
      return [];
    }
  };

  const loadCatalogPage = async (page) => {
    if (typeof fetch !== 'function' || typeof DOMParser === 'undefined') return [];
    const baseUrl = new URL(window.location.href);
    if (page <= 1) baseUrl.searchParams.delete('page');
    else baseUrl.searchParams.set('page', String(page));
    const response = await fetch(baseUrl, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Catalog page ${page} returned ${response.status}`);
    const html = await response.text();
    const documentPage = new DOMParser().parseFromString(html, 'text/html');
    const catalogScript = documentPage.querySelector('[data-titanium-catalog-page]');
    if (!catalogScript) throw new Error(`Catalog page ${page} did not include its product index`);
    return readCatalogScript(catalogScript);
  };

  const initRouteGroup = (root, group) => {
    const triggers = [...root.querySelectorAll(`[data-titanium-route-trigger="${group}"]`)];
    const panels = [...root.querySelectorAll(`[data-titanium-route-panel="${group}"]`)];
    if (!triggers.length || !panels.length) return;
    const routeQueryKey = group === 'kits' ? 'ti_kit' : 'ti_specialty';

    const activate = (routeId, moveToPanel = false) => {
      const selectedPanel = panels.find((panel) => panel.dataset.routeId === routeId) || panels[0];
      panels.forEach((panel) => {
        const active = panel === selectedPanel;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      triggers.forEach((trigger) => {
        const active = trigger.dataset.routeId === selectedPanel.dataset.routeId;
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-expanded', String(active));
        if (active) trigger.setAttribute('aria-current', 'true');
        else trigger.removeAttribute('aria-current');
      });
      if (moveToPanel) {
        const url = new URL(window.location.href);
        url.searchParams.set(routeQueryKey, selectedPanel.dataset.routeId);
        history.replaceState?.({}, '', `${url.pathname}${url.search}`);
      }
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', (event) => {
      event.preventDefault();
      activate(trigger.dataset.routeId, true);
    }));

    const requestedRoute = new URLSearchParams(window.location.search).get(routeQueryKey);
    activate(requestedRoute || panels.find((panel) => panel.classList.contains('is-active'))?.dataset.routeId || panels[0].dataset.routeId);
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const kitLocationDefinitions = [
    ['complete', 'Complete kits', /\b(complete|ultimate|full)\b.*\bkit\b|\bkit\b.*\b(complete|ultimate|full)\b/i],
    ['front-control-arms', 'Front control arms', /front.*control arm|control arm.*(?:front|pivot)/i],
    ['trailing-arms', 'Trailing arms', /trailing arm/i],
    ['radius-rods', 'Radius rods & toe links', /radius rod|toe link/i],
    ['front-shocks', 'Front shocks', /front.*shock|shock.*front/i],
    ['rear-shocks', 'Rear shocks', /rear.*shock|shock.*rear/i],
    ['sway-bars', 'Sway bars', /sway bar|stabilizer/i],
    ['steering', 'Steering', /steering|tie rod/i],
    ['cage-body', 'Cage & body', /cage|roof|body|door|bumper|skid|hood/i],
    ['drivetrain', 'Engine & drivetrain', /engine|drivetrain|differential|transmission|clutch/i],
    ['wheels-brakes', 'Wheels & brakes', /wheel|brake|hub|rotor|caliper/i]
  ];

  const classifyKitLocations = (title, publishedLocations = '') => {
    // Match each published location separately. A trailing-arm front pivot is
    // not a front control arm; complete-package status is additive.
    const heading = clean(title);
    const locations = clean(publishedLocations).split(/[,;|]/).map(clean);
    return kitLocationDefinitions.filter(([id, , pattern]) =>
      pattern.test(heading) || (id !== 'complete' && locations.some((location) => pattern.test(location)))
    ).map(([id]) => id);
  };

  if (testHook) Object.assign(testHook, { classifyKitLocations });

  const initKitSelector = async (root) => {
    const form = root.querySelector('[data-titanium-kit-selector]');
    const grid = root.querySelector('[data-titanium-kit-products]');
    if (!form || !grid || form.dataset.kitLoading) return;
    form.dataset.kitLoading = 'true';
    const guidance = root.querySelector('[data-titanium-kit-guidance]');
    const locationControl = root.querySelector('[data-titanium-kit-location]');
    let context = null;
    const entries = new Map();
    const readEntries = scope => {
      for (const entry of scope.querySelectorAll('[data-titanium-kit-entry]')) {
        if (!entry.dataset.productId || entries.has(entry.dataset.productId)) throw new Error('Duplicate kit identity.');
        let projection = null;
        try { projection = JSON.parse(entry.querySelector('[data-catalog-fitment]')?.textContent || 'null'); } catch (_) {}
        const locations = classifyKitLocations(entry.querySelector('h3')?.textContent, entry.dataset.kitLocation);
        entries.set(entry.dataset.productId,{entry,projection,locations});
      }
    };
    const filter = () => {
      let count = 0;
      for (const {entry,projection,locations} of entries.values()) {
        entry.querySelectorAll('a[href]').forEach(link=>{
          const original=link.dataset.fitmentOriginalHref || link.getAttribute('href');
          link.dataset.fitmentOriginalHref=original;
          link.href=context
            ? globalThis.AvidVehicleSelector.destination({url:original},context)
            : globalThis.AvidVehicleSelector.withNavigationContext(original).href;
        });
        const candidate = globalThis.AvidVehicleSelector.matchesDiscovery(projection,context);
        const locationMatches = !locationControl.value || locations.includes(locationControl.value);
        entry.hidden = !candidate || !locationMatches;
        if (!entry.hidden) count++;
      }
      guidance.textContent = `${count} of ${entries.size} kits shown. ${context ? 'Review each kit for compatibility and installation conditions.' : 'Choose your exact vehicle to narrow the catalog.'}`;
    };
    try {
      readEntries(grid);
      let next = grid.dataset.nextUrl;
      const seen = new Set();
      while (next) {
        const url = globalThis.AvidVehicleSelector.withNavigationContext(next);
        if (url.origin !== window.location.origin || seen.has(url.href) || seen.size >= 100) throw new Error('Kit pagination could not be reconciled.');
        seen.add(url.href);url.searchParams.set('section_id',grid.dataset.sectionId);
        const response = await fetch(url.href,{credentials:'same-origin'});
        if (!response.ok) throw new Error('A kit catalog page was unavailable.');
        const page = new DOMParser().parseFromString(await response.text(),'text/html').querySelector('[data-titanium-kit-products]');
        if (!page || Number(page.dataset.total) !== Number(grid.dataset.total)) throw new Error('The kit catalog changed during loading.');
        readEntries(page);
        grid.append(...page.querySelectorAll('[data-titanium-kit-entry]'));
        next=page.dataset.nextUrl;
      }
      if (entries.size !== Number(grid.dataset.total)) throw new Error('The complete kit catalog could not be verified.');
      const locations=new Set([...entries.values()].flatMap(({locations})=>locations));
      kitLocationDefinitions.filter(([value])=>locations.has(value)).forEach(([value,title])=>locationControl.add(new Option(title,value)));
      locationControl.addEventListener('change',filter);
      globalThis.AvidVehicleSelector.bind(form,undefined,undefined,value=>{context=value;filter();});
      const application = form.querySelector('[data-catalog-vehicle-application]');
      context=globalThis.AvidVehicleSelector.resolve({applicationId:application.value,year:form.querySelector('[data-catalog-vehicle-year]').value});
      root.querySelector('[data-titanium-kit-clear]').addEventListener('click',()=>{globalThis.AvidVehicleSelector.clear();context=null;locationControl.value='';filter();});
      void globalThis.AvidDealerPricing?.hydrate(grid);
      filter();
    } catch (error) {
      console.warn('AVID Titanium kit catalog could not load.',error);
      form.querySelectorAll('select,button').forEach(control=>{control.disabled=true;});
      guidance.textContent='The complete kit list could not load. Reload this page or browse the titanium kit collection.';
      guidance.dataset.catalogLoadFailed='true';
    }
  };

  const appendSpec = (list, name, value) => {
    if (!clean(value)) return;
    const entry = createElement('div');
    entry.append(createElement('dt', '', name), createElement('dd', '', value));
    list.append(entry);
  };

  const hardwareSpecRows = (product) => {
    const type = token(product.displayHardwareTypes?.[0] || product.hardwareTypes?.[0]);
    const browse = product.browseFacets || {};
    const common = [[product.materialLabel, product.displayMaterialGrade?.[0]], ['Finish', product.finish?.[0]]];
    if (type.includes('washer')) return [['Inside diameter', product.washerId?.[0] || product.diameter?.[0] || browse.diameter?.[0]], ['Outside diameter', product.washerOd?.[0]], ['Thickness', product.washerThickness?.[0] || browse.overallLength?.[0]], ...common];
    if (type.includes('nut')) return [['Thread', product.displayPitch?.[0] || browse.pitch?.[0]], ['Diameter', product.diameter?.[0] || browse.diameter?.[0]], ['Nut style', customerLabel(product.nutStyle?.[0])], ['Locking style', customerLabel(product.lockingStyle?.[0])], ['Wrench size', product.wrenchSize?.[0] || browse.wrenchSize?.[0]], ['Thread direction', customerLabel(product.threadDirection?.[0])], ...common];
    if (type.includes('spacer') || type.includes('misalignment') || type.includes('clevis')) return [['Bearing ID', product.bearingId?.[0]], ['Bolt hole', product.boltHole?.[0]], ['Stack width', product.stackWidth?.[0]], ['Ball radius', product.ballRadius?.[0]], ...common];
    const gripValue = product.grip?.[0] || browse.grip?.[0];
    const displayedGrip = /^0(?:\.0+)?\s+(?:mm|in)$/i.test(clean(gripValue)) ? 'Fully threaded' : gripValue;
    return [['Diameter', product.diameter?.[0] || browse.diameter?.[0]], ['Pitch / TPI', product.displayPitch?.[0] || browse.pitch?.[0]], ['Overall length', product.displayOverallLength?.[0] || browse.overallLength?.[0]], ['Shank / grip', displayedGrip], ['Threaded length', product.threadLength?.[0] || browse.threadLength?.[0]], ['Head style', customerLabel(product.headStyle?.[0] || browse.headStyle?.[0])], ['Drive style', displayFacetValue(product.driveStyle?.[0] || browse.driveStyle?.[0])], ['Wrench size', product.wrenchSize?.[0] || browse.wrenchSize?.[0]], ['Head diameter', product.headDiameter?.[0]], ['Head height', product.headHeight?.[0]], ['Flange diameter', product.flangeDiameter?.[0]], ...common];
  };

  const titaniumFinishCatalog = (root, scope = 'individual') => [...root.querySelectorAll(`[data-titanium-finish-catalog][data-finish-scope="${scope}"] [data-titanium-finish-entry]`)]
    .map((entry) => ({
      label: clean(entry.dataset.finishLabel),
      key: clean(entry.dataset.finishKey),
      minimumCents: Number(entry.dataset.finishMinimumCents || 500),
      unitCents: Number(entry.dataset.finishUnitCents || 37),
      minimumVariantId: clean(entry.dataset.finishMinimumVariantId),
      pieceVariantId: clean(entry.dataset.finishPieceVariantId),
      feeReady: entry.dataset.finishFeeReady === 'true',
      image: clean(entry.dataset.finishImage),
      available: entry.dataset.finishAvailable === 'true'
    }))
    .filter((entry) => entry.label && entry.available);

  const appendAnodizingControls = (container, root, { compact = false, scope = 'individual', pieceCount = 0 } = {}) => {
    const options = titaniumFinishCatalog(root, scope);
    const eligiblePiecesPerUnit = Math.max(0, Number.parseInt(pieceCount, 10) || 0);
    const fieldset = createElement('fieldset', `titanium-anodizing titanium-anodizing--dynamic${compact ? ' titanium-anodizing--compact' : ''}`);
    fieldset.dataset.anodizingScope = scope;
    fieldset.append(createElement('legend', '', 'Titanium finish'));
    const optionsNode = createElement('div', 'titanium-anodizing__options');
    optionsNode.setAttribute('role', 'radiogroup');
    optionsNode.setAttribute('aria-label', 'Titanium finish');
    const groupName = `avid_titanium_finish_${scope}_${Math.random().toString(36).slice(2, 9)}`;
    const inputs = options.map((option, index) => {
      const label = createElement('label', 'titanium-anodizing__option');
      label.dataset.finishLabel = option.label;
      label.dataset.finishAvailable = String(option.available);
      const input = document.createElement('input');
      input.type = 'radio'; input.name = groupName; input.value = option.label; input.checked = index === 0;
      const media = createElement('span', 'titanium-anodizing__media');
      const image = document.createElement('img');
      image.src = option.image; image.alt = `${option.label} titanium finish`; image.loading = 'lazy'; image.width = 240; image.height = 240;
      media.append(image);
      const copy = createElement('span', 'titanium-anodizing__copy');
      copy.append(createElement('strong', '', option.label));
      label.append(input, media, copy); optionsNode.append(label);
      return input;
    });
    const status = createElement('p', 'titanium-anodizing__status', 'Raw Ti selected. No anodizing charge.');
    status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    fieldset.append(optionsNode, status); container.append(fieldset);
    const selected = () => {
      const input = inputs.find((candidate) => candidate.checked);
      return options.find((option) => option.label === input?.value) || options[0] || null;
    };
    const chargeCents = (quantity = 1) => {
      const option = selected();
      if (!option || option.label === 'Raw Ti') return 0;
      const pieces = eligiblePiecesPerUnit * Math.max(1, Number.parseInt(quantity, 10) || 1);
      return pieces > 0 ? Math.max(option.minimumCents, option.unitCents * pieces) : 0;
    };
    const refresh = (quantity = 1) => {
      const option = selected();
      if (!option || option.label === 'Raw Ti') {
        status.textContent = 'Raw Ti selected. No anodizing charge.';
        return Boolean(option?.available);
      }
      const pieces = eligiblePiecesPerUnit * Math.max(1, Number.parseInt(quantity, 10) || 1);
      const charge = chargeCents(quantity);
      if (!pieces) status.textContent = `${option.label} selected. The reviewed anodizable-piece count is missing, so checkout is blocked.`;
      else if (!option.feeReady) status.textContent = `${option.label} selected. ${pieces} anodizable ${pieces === 1 ? 'piece' : 'pieces'} = ${formatCents(charge)}. Online checkout remains unavailable until AVID activates the governed fee service.`;
      else status.textContent = `${option.label} selected. ${pieces} anodizable ${pieces === 1 ? 'piece' : 'pieces'}. Estimated service: ${formatCents(charge)}. Same-color items combine in the cart with one minimum per color.`;
      return Boolean(option.available && pieces > 0 && option.feeReady && option.minimumVariantId && option.pieceVariantId);
    };
    inputs.forEach((input) => input.addEventListener('change', refresh));
    return { inputs, selected, refresh, chargeCents, eligiblePiecesPerUnit, isAnodized: () => selected()?.label !== 'Raw Ti' };
  };

  const addTitaniumHardwareToCart = async ({ root, product, variant, quantity, anodizing, trigger, source }) => {
    const count = Math.max(1, Number.parseInt(quantity, 10) || 1);
    const anodized = anodizing?.isAnodized() === true;
    if (anodized && !anodizing.refresh(count)) {
      const selected = anodizing?.selected();
      const pieces = (anodizing?.eligiblePiecesPerUnit || 0) * count;
      if (!pieces) throw new Error('The reviewed anodizable-piece count is missing, so colored checkout is blocked.');
      if (!selected?.feeReady) throw new Error('Online checkout for colored finishes remains unavailable until AVID activates the governed fee service.');
      throw new Error('The selected anodizing option is not ready for checkout.');
    }
    const selectedFinish = anodizing?.selected() || { label: 'Raw Ti' };
    const groupId = `avid-ti-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const hardwareProperties = {
      'Titanium finish': selectedFinish.label, '_AVID Titanium group': groupId,
      '_AVID anodizable pieces per unit': String(anodizing?.eligiblePiecesPerUnit || 0),
      '_AVID Titanium purchase surface': source,
      ...(anodized ? { '_AVID Titanium selected finish image': selectedFinish.image || '' } : {})
    };
    const message = `${product.title} added to cart${anodized ? ` with ${selectedFinish.label}` : ''}.`;
    if (!window.avidCart?.addTitaniumItem) throw new Error('The cart is still loading. Please try again.');
    return window.avidCart.addTitaniumItem({ id: variant.id, quantity: count, properties: hardwareProperties }, { open: true, trigger, announcement: message });
  };

  const renderProductCard = (product, onOpen, root) => {
    const article = createElement('article', 'titanium-product-card');
    const imageLink = createElement('a', 'titanium-product-card__image');
    imageLink.href = product.url;
    imageLink.setAttribute('aria-label', `View ${product.title}`);
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.srcset = responsiveSrcset(product.image, [240, 360, 540, 720]);
      image.sizes = '(max-width: 749px) 96px, (max-width: 1199px) 34vw, 22vw';
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 720;
      image.height = 540;
      imageLink.append(image);
    } else {
      imageLink.append(createElement('span', '', 'AVID Ti'));
    }

    const body = createElement('div', 'titanium-product-card__body');
    const standard = product.displayStandards.length ? product.displayStandards.map(customerLabel).join(' / ') : 'Specifications incomplete';
    body.append(createElement('span', 'titanium-product-card__standard', standard));
    const heading = createElement('h3');
    const titleLink = createElement('a', '', product.title);
    titleLink.href = product.url;
    heading.append(titleLink);
    body.append(heading);

    const specs = createElement('dl', 'titanium-product-card__specs');
    const cardRows = hardwareSpecRows(product);
    cardRows.slice(0, 10).forEach(([name, value]) => appendSpec(specs, name, value));
    body.append(specs);

    const footer = createElement('div', 'titanium-product-card__footer');
    const fallbackPriceText = product.priceReviewRequired ? 'Contact AVID for pricing' : product.priceLabel || 'See product';
    if (product.priceReviewRequired) footer.append(createElement('strong', 'product-card__pricing-review', fallbackPriceText));
    else {
      const availableVariants = (product.variants || []).filter((variant) => variant.available && Number(variant.price) > 0);
      const displayVariants = availableVariants.length ? availableVariants : (product.variants || []);
      const displayVariant = displayVariants.reduce((lowest, variant) => Number(variant.price) < Number(lowest.price) ? variant : lowest, displayVariants[0]);
      const variantIds = displayVariant?.id ? [displayVariant.id] : [];
      const priceVaries = new Set(displayVariants.map((variant) => Number(variant.price))).size > 1;
      const price = globalThis.AvidDealerPricing?.createPriceElement?.({
        variantIds,
        priceText: displayVariant?.priceLabel || product.priceLabel || 'See product',
        priceCents: Number(displayVariant?.price || 0),
        compareAtPriceCents: Number(displayVariant?.compareAtPrice || 0),
        compareAtPriceText: displayVariant?.compareAtPriceLabel || '',
        priceVaries
      });
      footer.append(price || createElement('strong', '', product.priceLabel || 'See product'));
    }
    const viewLabel = product.available ? 'Quick detail' : 'View availability';
    const viewLink = createElement('button', '', viewLabel);
    viewLink.type = 'button';
    viewLink.addEventListener('click', () => onOpen(product, viewLink));
    footer.append(viewLink);
    body.append(footer);
    article.append(imageLink, body);
    return article;
  };

  const productFromQuickCard = (card) => {
    const available = card.dataset.productAvailable === 'true';
    const priceValid = card.dataset.productPriceValid === 'true';
    const simple = card.dataset.productSimple === 'true';
    const price = Number(card.dataset.productPrice || 0);
    const kind = card.dataset.titaniumQuickCard || 'product';
    const variantId = clean(card.dataset.productVariantId);
    const quickSpecs = kind === 'kit'
      ? [['Fitment', card.dataset.productFitment], ['Years', card.dataset.productYears], ['Kit location', card.dataset.productLocation], ['Pieces', card.dataset.productPieces], ['Availability', available ? 'Available' : 'Currently unavailable'], ['Lead time', card.dataset.productLeadTime]]
      : [['Application', card.dataset.productFitment], ['Component type', card.dataset.productLocation], ['Material', card.dataset.productMaterial], ['Availability', available ? 'Available' : 'Currently unavailable']];
    return {
      kind,
      title: card.dataset.productTitle,
      url: card.dataset.productUrl,
      sku: card.dataset.productSku,
      image: card.dataset.productImage,
      imageAlt: card.dataset.productImageAlt,
      summary: card.dataset.productSummary,
      reviewed: true,
      available,
      priceReviewRequired: !priceValid,
      priceLabel: card.dataset.productPriceLabel,
      anodizablePieceCount: Number.parseInt(card.dataset.productAnodizablePieces, 10) || 0,
      quickSpecs,
      requiresConfiguration: !simple || kind === 'kit',
      requiresFitmentReview: kind === 'kit',
      options: [],
      variants: variantId ? [{ id: variantId, available, price, priceLabel: card.dataset.productPriceLabel, options: [] }] : []
    };
  };

  const initHardwareDrawer = (root) => {
    const drawer = root.querySelector('[data-titanium-hardware-drawer]');
    const backdrop = root.querySelector('.titanium-hardware-drawer__backdrop');
    const body = root.querySelector('[data-titanium-drawer-body]');
    const title = root.querySelector('[data-titanium-drawer-title]');
    const eyebrow = root.querySelector('[data-titanium-drawer-eyebrow]');
    if (!drawer || !body || !title) return () => {};
    let trigger = null;
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      backdrop?.setAttribute('hidden', '');
      document.body.classList.remove('titanium-drawer-is-open');
      trigger?.focus?.({ preventScroll: true });
    };
    root.querySelectorAll('[data-titanium-drawer-close]').forEach((control) => control.addEventListener('click', close));
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    const open = (product, source) => {
      trigger = source;
      title.textContent = product.title;
      if (eyebrow) eyebrow.textContent = product.kind === 'kit' ? 'Titanium kit quick detail' : product.kind === 'specialty' ? 'Specialty titanium quick detail' : 'Hardware quick detail';
      body.replaceChildren();
      const identity = createElement('div', 'titanium-hardware-drawer__identity');
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.title;
        image.width = 720;
        image.height = 540;
        identity.append(image);
      }
      const copy = createElement('div');
      copy.append(createElement('p', 'titanium-hardware-drawer__sku', product.sku || 'See product for SKU'));
      copy.append(createElement('p', '', product.summary || 'Confirm the complete specification before ordering.'));
      identity.append(copy);
      body.append(identity);

      const specs = createElement('dl', 'titanium-hardware-drawer__specs');
      const drawerSpecs = product.quickSpecs || [
        [product.reviewed ? 'Hardware type' : 'Catalog class', customerLabel(product.displayHardwareTypes?.[0] || product.browseHardwareTypes?.[0])],
        ['Thread system', product.displayStandards?.map(customerLabel).join(' / ')],
        ...hardwareSpecRows(product),
        ['Availability', product.available ? 'Available' : 'Currently unavailable']
      ];
      drawerSpecs.forEach(([name, value]) => appendSpec(specs, name, value));
      body.append(specs);
      const purchase = createElement('form', 'titanium-hardware-drawer__purchase');
      const optionControls = (Array.isArray(product.options) ? product.options : [])
        .map((option, index) => ({ option, index }))
        .filter(({ option }) => Array.isArray(option.values) && option.values.length > 0 && !(/^title$/i.test(clean(option.name)) && option.values.every((value) => /^default title$/i.test(clean(value)))))
        .map(({ option, index }) => {
        const labelNode = createElement('label');
        labelNode.append(createElement('span', '', option.name));
        const select = document.createElement('select');
        select.dataset.optionIndex = String(index);
        option.values.forEach((value) => select.add(new Option(value, value)));
        labelNode.append(select);
        purchase.append(labelNode);
        return { control: select, optionIndex: index };
      });
      const anodizing = appendAnodizingControls(purchase, root, { compact: true, scope: product.kind === 'kit' ? 'kit' : 'individual', pieceCount: product.anodizablePieceCount });
      const quantityLabel = createElement('label');
      quantityLabel.append(createElement('span', '', 'Quantity'));
      const quantity = document.createElement('input');
      quantity.type = 'number'; quantity.min = '1'; quantity.value = '1'; quantity.inputMode = 'numeric';
      quantityLabel.append(quantity); purchase.append(quantityLabel);
      const total = createElement('strong', 'titanium-hardware-drawer__total');
      const status = createElement('p', 'titanium-hardware-drawer__status');
      status.setAttribute('role', 'status');
      const add = createElement('button', 'titanium-center__button titanium-center__button--primary', 'Add to cart');
      add.type = 'submit';
      const details = createElement('a', 'titanium-center__button titanium-center__button--secondary', 'Full product details');
      details.href = product.url;
      if (product.requiresConfiguration) {
        add.hidden = true;
        add.disabled = true;
        details.className = 'titanium-center__button titanium-center__button--primary';
        details.textContent = product.kind === 'kit' ? 'Configure Kit' : 'Choose Product Options';
        quantityLabel.hidden = true;
        if (product.priceReviewRequired) total.textContent = 'Price verification required';
        else {
          const displayVariants = (product.variants || []).filter((variant) => variant.available && Number(variant.price) > 0);
          const displayVariant = displayVariants.reduce((lowest, variant) => Number(variant.price) < Number(lowest.price) ? variant : lowest, displayVariants[0]);
          const price = globalThis.AvidDealerPricing?.createPriceElement?.({
            variantIds: displayVariant?.id ? [displayVariant.id] : [],
            priceText: displayVariant?.priceLabel || product.priceLabel || 'See product for price',
            priceCents: Number(displayVariant?.price || 0),
            compareAtPriceCents: Number(displayVariant?.compareAtPrice || 0),
            compareAtPriceText: displayVariant?.compareAtPriceLabel || '',
            priceVaries: new Set(displayVariants.map((variant) => Number(variant.price))).size > 1
          });
          if (price) total.replaceChildren(price);
          else total.textContent = product.priceLabel || 'See product for price';
        }
        status.textContent = product.requiresFitmentReview ? 'Confirm fitment before ordering. Review the complete vehicle application and required options on the product page.' : 'This product requires choices that must be completed on the full product page.';
      }
      purchase.append(total, status, add, details);
      body.append(purchase);

      const resolveVariant = () => {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        if (!optionControls.length) return variants.find((variant) => variant.id);
        return variants.find((variant) => optionControls.every(({ control, optionIndex }) => (variant.options || [])[optionIndex] === control.value));
      };
      const refresh = () => {
        if (product.requiresConfiguration) {
          status.textContent = product.requiresFitmentReview ? 'Confirm fitment before ordering. Review the complete vehicle application and required options on the product page.' : 'This product requires choices that must be completed on the full product page.';
          return null;
        }
        const variant = resolveVariant();
        const count = Math.max(1, Number.parseInt(quantity.value, 10) || 1);
        quantity.value = String(count);
        const baseValid = Boolean(!product.priceReviewRequired && variant?.id && variant.available && Number(variant.price) > 0);
        const finishValid = !anodizing || anodizing.refresh(count);
        const valid = Boolean(baseValid && finishValid);
        if (add.isConnected) add.disabled = !valid;
        const anodizingTotal = anodizing?.chargeCents(count) || 0;
        const formattedTotal = baseValid
          ? new Intl.NumberFormat(undefined, { style: 'currency', currency: root.dataset.currency || 'USD' }).format(((Number(variant.price) * count) + anodizingTotal) / 100)
          : '';
        if (baseValid) {
          const priceText = `${variant.priceLabel} each · ${formattedTotal} total`;
          const price = globalThis.AvidDealerPricing?.createPriceElement?.({
            variantIds: variant.id,
            priceText,
            priceCents: Number(variant.price) * count + anodizingTotal,
            compareAtPriceCents: Number(variant.compareAtPrice || 0) * count + anodizingTotal,
            compareAtPriceText: variant.compareAtPriceLabel ? `${variant.compareAtPriceLabel} each` : '',
            priceMultiplier: count,
            priceAdditionCents: anodizingTotal
          });
          if (price) {
            total.replaceChildren(price);
            void globalThis.AvidDealerPricing.hydrate(price);
          } else total.textContent = priceText;
        } else total.textContent = 'Price or availability verification required';
        if (valid) status.textContent = 'Ready to add.';
        else if (baseValid && anodizing?.isAnodized() && anodizing.eligiblePiecesPerUnit > 0) status.textContent = 'The exact anodizing charge is shown above. Colored checkout remains blocked until AVID activates the governed fee service.';
        else if (baseValid && anodizing?.isAnodized()) status.textContent = 'The reviewed anodizable-piece count is missing, so colored checkout is blocked.';
        else status.textContent = 'This selection cannot be added until availability and price are verified.';
        return variant;
      };
      optionControls.forEach(({ control }) => control.addEventListener('change', refresh));
      anodizing?.inputs.forEach((input) => input.addEventListener('change', refresh));
      quantity.addEventListener('input', refresh);
      purchase.addEventListener('submit', async (event) => {
        event.preventDefault();
        const variant = refresh();
        if (product.requiresConfiguration || !variant || add.disabled) return;
        add.disabled = true; status.textContent = 'Adding to cart...';
        try {
          await addTitaniumHardwareToCart({ root, product, variant, quantity: quantity.value, anodizing, trigger: source, source: 'quick_detail' });
          close();
        } catch (error) {
          status.textContent = error.message || 'This hardware could not be added.';
          add.disabled = false;
        }
      });
      refresh();
      drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); backdrop?.removeAttribute('hidden'); document.body.classList.add('titanium-drawer-is-open');
      window.setTimeout(() => drawer.querySelector('[data-titanium-drawer-close]')?.focus({ preventScroll: true }), 20);
    };
    return open;
  };

  const initLaneQuickCards = (root, openDrawer) => {
    root.querySelectorAll('[data-titanium-quick-card]').forEach((card) => {
      const trigger = card.querySelector('[data-titanium-quick-open]');
      trigger?.addEventListener('click', () => openDrawer(productFromQuickCard(card), trigger));
    });
  };

  const initSpecialtySort = (root) => {
    const control = root.querySelector('[data-titanium-specialty-sort]');
    if (!control) return;
    const apply = () => {
      root.querySelectorAll('[data-titanium-route-panel="specialty"] .titanium-lane-products').forEach((grid) => {
        const cards = [...grid.querySelectorAll('[data-titanium-quick-card="specialty"]')];
        cards.sort((left, right) => {
          const leftPrice = Number(left.dataset.productPrice || 0);
          const rightPrice = Number(right.dataset.productPrice || 0);
          if (control.value === 'price-asc') return (leftPrice || Number.POSITIVE_INFINITY) - (rightPrice || Number.POSITIVE_INFINITY);
          if (control.value === 'price-desc') return rightPrice - leftPrice;
          return clean(left.dataset.productTitle).localeCompare(clean(right.dataset.productTitle), undefined, { numeric: true });
        });
        grid.append(...cards);
      });
    };
    control.addEventListener('change', apply);
    apply();
  };

  const initTitaniumCenter = async (root) => {
    if (root.dataset.titaniumCenterReady === 'true') return;
    root.dataset.titaniumCenterReady = 'true';
    root.classList.add('is-enhanced');
    initRouteGroup(root, 'specialty');
    initKitSelector(root);
    const openHardwareDrawer = initHardwareDrawer(root);
    initLaneQuickCards(root, openHardwareDrawer);
    initSpecialtySort(root);

    const form = root.querySelector('[data-titanium-filter-form]');
    const initialScript = root.querySelector('[data-titanium-catalog-page]');
    const resultsRegion = root.querySelector('[data-titanium-results-region]');
    const resultsGrid = root.querySelector('[data-titanium-results]');
    const countNode = root.querySelector('[data-titanium-result-count]');
    const statusNode = root.querySelector('[data-titanium-data-status]');
    const emptyNode = root.querySelector('[data-titanium-empty]');
    const showMore = root.querySelector('[data-titanium-show-more]');
    const retryCatalog = root.querySelector('[data-titanium-retry]');
    const chips = root.querySelector('[data-titanium-filter-chips]');
    const search = root.querySelector('[data-titanium-search]');
    const available = root.querySelector('[data-titanium-available]');
    const sort = root.querySelector('[data-titanium-sort]');
    const standards = [...root.querySelectorAll('[data-titanium-standard]')];
    const facetControls = [...root.querySelectorAll('[data-titanium-filter]')];
    if (!form || !initialScript || !resultsGrid) return;

    const selectAllLabel = clean(root.dataset.filterSelectAll) || 'Select all';
    const clearLabel = clean(root.dataset.filterClear) || 'Clear';
    const selectedCountLabel = clean(root.dataset.filterSelectedCount) || '__count__ selected';
    const facetWidgets = new Map();
    let openFacetWidget = null;

    const closeFacetWidget = (widget, restoreFocus = false) => {
      if (!widget || widget.menu.hidden) return;
      widget.menu.hidden = true;
      widget.wrapper.classList.remove('is-open');
      widget.trigger.setAttribute('aria-expanded', 'false');
      if (openFacetWidget === widget) openFacetWidget = null;
      if (restoreFocus) widget.trigger.focus();
    };

    const closeOtherFacetWidgets = (current) => {
      facetWidgets.forEach((widget) => {
        if (widget !== current) closeFacetWidget(widget);
      });
    };

    const setFacetSelection = (control, values) => {
      const selected = new Set(toList(values).map(token));
      [...control.options].forEach((option) => {
        option.selected = Boolean(option.value) && selected.has(token(option.value));
      });
    };

    const pageSize = Number(root.dataset.pageSize || 20);
    const sourceCount = Number(root.dataset.sourceCount || 0);
    const totalPages = Number(initialScript.dataset.totalPages || 1);
    const loadedPages = new Set([Number(initialScript.dataset.currentPage || 1)]);
    let visibleCount = pageSize;
    let products = uniqueCatalogProducts(readCatalogScript(initialScript).map(normalizeProduct));
    let filteredProducts = [];
    let catalogComplete = isCatalogReconciled(products, loadedPages.size, totalPages, sourceCount);
    let catalogLoadError = '';
    const initialParams = new URLSearchParams(window.location.search);
    const exactParamKeys = facetControls
      .filter((control) => !browseFacetKeys.has(control.dataset.titaniumFilter))
      .map((control) => `ti_${control.dataset.titaniumFilter}`);
    const requestedExactFilter = exactParamKeys.some((key) => clean(initialParams.get(key)));

    const setStatus = (message) => {
      if (statusNode) statusNode.textContent = message;
    };

    const optionLabel = (value, key = '') => {
      const displayed = displayFacetValue(value);
      if (key === 'grip' && /^0(?:\.0+)?\s+(?:mm|in)$/i.test(displayed)) return `Fully threaded (${displayed} grip)`;
      return displayed;
    };

    const initializeFacetWidget = (control, index) => {
      const field = control.closest('.titanium-finder__field');
      const fieldLabel = clean(field?.querySelector('span')?.textContent) || optionLabel(control.dataset.titaniumFilter);
      const emptyLabel = clean(control.options[0]?.textContent) || `All ${fieldLabel.toLowerCase()}`;
      const widgetId = `TitaniumFacet-${clean(root.dataset.sectionId || 'section')}-${index}`;
      const wrapper = createElement('div', 'titanium-multiselect');
      const trigger = createElement('button', 'titanium-multiselect__trigger');
      const triggerText = createElement('span', 'titanium-multiselect__trigger-text', emptyLabel);
      const caret = createElement('span', 'titanium-multiselect__caret', '⌄');
      const menu = createElement('div', 'titanium-multiselect__menu');
      const actions = createElement('div', 'titanium-multiselect__actions');
      const selectAll = createElement('button', '', selectAllLabel);
      const clear = createElement('button', '', clearLabel);
      const optionsList = createElement('div', 'titanium-multiselect__options');

      control.multiple = true;
      control.hidden = true;
      trigger.type = 'button';
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', widgetId);
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-label', `${fieldLabel}: ${emptyLabel}`);
      caret.setAttribute('aria-hidden', 'true');
      menu.id = widgetId;
      menu.hidden = true;
      menu.setAttribute('role', 'group');
      menu.setAttribute('aria-label', `${fieldLabel} options`);
      selectAll.type = 'button';
      clear.type = 'button';
      actions.append(selectAll, clear);
      menu.append(actions, optionsList);
      trigger.append(triggerText, caret);
      wrapper.append(trigger, menu);
      control.insertAdjacentElement('afterend', wrapper);

      const widget = {
        control,
        emptyLabel,
        fieldLabel,
        menu,
        optionsList,
        trigger,
        triggerText,
        wrapper,
        rebuild: null,
        sync: null
      };

      widget.sync = () => {
        const selected = selectedFacetValues(control);
        const summary = selected.length === 0
          ? emptyLabel
          : selected.length === 1
            ? optionLabel(selected[0], control.dataset.titaniumFilter)
            : selectedCountLabel.replace('__count__', String(selected.length));
        triggerText.textContent = summary;
        trigger.setAttribute('aria-label', `${fieldLabel}: ${summary}`);
        trigger.classList.toggle('has-selection', selected.length > 0);
        optionsList.querySelectorAll('[data-titanium-facet-option]').forEach((checkbox) => {
          checkbox.checked = selected.some((value) => token(value) === token(checkbox.value));
        });
      };

      widget.rebuild = () => {
        optionsList.replaceChildren();
        [...control.options].filter((option) => option.value).forEach((option) => {
          const optionRow = createElement('label', 'titanium-multiselect__option');
          const checkbox = document.createElement('input');
          checkbox.type = 'checkbox';
          checkbox.value = option.value;
          checkbox.checked = option.selected;
          checkbox.dataset.titaniumFacetOption = '';
          checkbox.addEventListener('change', () => {
            option.selected = checkbox.checked;
            if (control.options[0]) control.options[0].selected = false;
            widget.sync();
            control.dispatchEvent(new Event('change', { bubbles: true }));
          });
          optionRow.append(checkbox, createElement('span', '', optionLabel(option.value, control.dataset.titaniumFilter)));
          optionsList.append(optionRow);
        });
        widget.sync();
      };

      trigger.addEventListener('click', () => {
        const willOpen = menu.hidden;
        closeOtherFacetWidgets(widget);
        menu.hidden = !willOpen;
        wrapper.classList.toggle('is-open', willOpen);
        trigger.setAttribute('aria-expanded', String(willOpen));
        openFacetWidget = willOpen ? widget : null;
        if (willOpen) optionsList.querySelector('input')?.focus();
      });
      selectAll.addEventListener('click', () => {
        setFacetSelection(control, [...control.options].filter((option) => option.value).map((option) => option.value));
        widget.sync();
        control.dispatchEvent(new Event('change', { bubbles: true }));
      });
      clear.addEventListener('click', () => {
        setFacetSelection(control, []);
        widget.sync();
        control.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrapper.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || menu.hidden) return;
        event.preventDefault();
        closeFacetWidget(widget, true);
      });

      facetWidgets.set(control, widget);
      return widget;
    };

    facetControls.forEach(initializeFacetWidget);
    document.addEventListener('click', (event) => {
      if (openFacetWidget && !openFacetWidget.wrapper.contains(event.target)) closeFacetWidget(openFacetWidget);
    });

    const populateFacets = () => {
      // Two passes first remove stale combinations and then repopulate every
      // dropdown from the surviving filter state. This prevents any option
      // from being offered when selecting it would return zero products.
      for (let pass = 0; pass < 2; pass += 1) {
        facetControls.forEach((control) => {
          const key = control.dataset.titaniumFilter;
          const current = readFilters();
          current.facets[key] = [];
          const sourceProducts = products.filter((product) => matchesFilters(product, current));
          const values = [...new Map(sourceProducts.flatMap((product) => product.browseFacets?.[key] || []).map(clean).filter(Boolean).map((value) => [token(displayFacetValue(value)), value])).values()]
            .sort((a, b) => numeric(a) - numeric(b) || a.localeCompare(b, undefined, { numeric: true }));
          const selected = new Set(selectedFacetValues(control).map(token));
          [...control.options].slice(1).forEach((option) => option.remove());
          values.forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = optionLabel(value, key);
            option.selected = selected.has(token(value));
            control.append(option);
          });
          const field = control.closest('.titanium-finder__field');
          if (field) field.hidden = values.length === 0;
          facetWidgets.get(control)?.rebuild();
        });
      }
    };

    const readFilters = () => ({
      standard: standards.find((control) => control.checked)?.value || '',
      search: clean(search?.value),
      available: Boolean(available?.checked),
      facets: Object.fromEntries(facetControls.map((control) => [control.dataset.titaniumFilter, selectedFacetValues(control)]))
    });

    const updateUrl = (filters) => {
      if (!history.replaceState) return;
      const url = new URL(window.location.href);
      const entries = {
        ti_standard: filters.standard,
        ti_q: filters.search,
        ti_available: filters.available ? '1' : ''
      };
      Object.entries(entries).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        } else if (!catalogComplete && requestedExactFilter && exactParamKeys.includes(key) && clean(initialParams.get(key))) {
          url.searchParams.set(key, initialParams.get(key));
        } else {
          url.searchParams.delete(key);
        }
      });
      Object.entries(filters.facets).forEach(([facetKey, values]) => {
        const key = `ti_${facetKey}`;
        const selected = toList(values);
        url.searchParams.delete(key);
        if (selected.length) {
          selected.forEach((value) => url.searchParams.append(key, value));
        } else if (!catalogComplete && requestedExactFilter && clean(initialParams.get(key))) {
          initialParams.getAll(key).forEach((value) => url.searchParams.append(key, value));
        }
      });
      history.replaceState({}, '', url);
    };

    const applyUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const standard = params.get('ti_standard') || '';
      const standardControl = standards.find((control) => control.value === standard) || standards[0];
      if (standardControl) standardControl.checked = true;
      if (search) search.value = params.get('ti_q') || '';
      if (available) available.checked = params.get('ti_available') === '1';
      facetControls.forEach((control) => {
        const availableValues = new Set([...control.options].map((option) => token(option.value)));
        const values = params.getAll(`ti_${control.dataset.titaniumFilter}`)
          .filter((value) => availableValues.has(token(value)));
        setFacetSelection(control, values);
        facetWidgets.get(control)?.sync();
      });
    };

    const renderChips = (filters) => {
      chips.replaceChildren();
      const entries = [
        ['standard', filters.standard, () => { standards[0].checked = true; }],
        ['search', filters.search, () => { search.value = ''; }],
        ['availability', filters.available ? 'Available now' : '', () => { available.checked = false; }],
        ...facetControls.flatMap((control) => selectedFacetValues(control).map((value) => [
          control.dataset.titaniumFilter,
          value,
          () => {
            const remaining = selectedFacetValues(control).filter((selected) => token(selected) !== token(value));
            setFacetSelection(control, remaining);
            facetWidgets.get(control)?.sync();
          }
        ]))
      ].filter(([, value]) => value);
      entries.forEach(([key, value, clearValue]) => {
        const chip = createElement('button', '', `${key === 'search' ? 'Search: ' : ''}${optionLabel(value, key)} x`);
        chip.type = 'button';
        chip.addEventListener('click', () => {
          clearValue();
          visibleCount = pageSize;
          populateFacets();
          render();
        });
        chips.append(chip);
      });
    };

    const render = () => {
      const filters = readFilters();
      filteredProducts = sortProducts(products.filter((product) => matchesFilters(product, filters)), sort?.value || 'relevance');
      resultsGrid.replaceChildren(...filteredProducts.slice(0, visibleCount).map((product) => renderProductCard(product, openHardwareDrawer, root)));
      const total = filteredProducts.length;
      countNode.textContent = catalogComplete
        ? `${total} ${total === 1 ? 'hardware product' : 'hardware products'} matched`
        : `${total} matched in ${products.length} loaded products`;
      emptyNode.hidden = total > 0;
      resultsGrid.hidden = total === 0;
      const hiddenLoadedMatches = visibleCount < total;
      showMore.hidden = !hiddenLoadedMatches;
      showMore.textContent = 'Show more matched hardware';
      renderChips(filters);
      facetWidgets.forEach((widget) => widget.sync());
      updateUrl(filters);
      resultsRegion.setAttribute('aria-busy', 'false');
      setStatus(catalogLoadError || (catalogComplete
        ? (hasExactFilter(filters)
          ? 'Filters match the complete published Titanium catalog. Open Quick detail to review and purchase.'
          : 'Complete Titanium hardware catalog loaded and ready to filter.')
         : `Only ${products.length}${sourceCount ? ` of ${sourceCount}` : ''} products could be loaded. Exact dimensional filtering is temporarily unavailable.`));
      if (retryCatalog) retryCatalog.hidden = catalogComplete || !catalogLoadError;
    };

    const queueRender = (() => {
      let timer;
      return () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          visibleCount = pageSize;
          populateFacets();
          render();
        }, 100);
      };
    })();

    let catalogLoadPromise;
    const completeCatalog = async ({ forceReload = false } = {}) => {
      if (catalogComplete) return;
      if (catalogLoadPromise) return catalogLoadPromise;
      if (forceReload) {
        loadedPages.clear();
        products = [];
      }
      catalogLoadError = '';
      resultsRegion.setAttribute('aria-busy', 'true');
      if (retryCatalog) retryCatalog.hidden = true;
      catalogLoadPromise = (async () => {
        const remainingPages = Array.from({ length: totalPages }, (_, index) => index + 1)
          .filter((page) => !loadedPages.has(page));
        setStatus(`Loading the complete AVID Ti hardware index (${loadedPages.size} of ${totalPages} pages loaded)...`);
        try {
          for (const page of remainingPages) {
            const pageProducts = await loadCatalogPage(page);
            loadedPages.add(page);
            products.push(...pageProducts.map((product, index) => normalizeProduct(product, products.length + index)));
            setStatus(`Loading the complete AVID Ti hardware index (${loadedPages.size} of ${totalPages} pages loaded)...`);
          }
          products = uniqueCatalogProducts(products).map((product, index) => ({ ...product, index }));
          catalogComplete = isCatalogReconciled(products, loadedPages.size, totalPages, sourceCount);
          catalogLoadError = catalogComplete
            ? ''
            : `The complete hardware index could not be verified (${products.length} unique of ${sourceCount} expected). Exact filtering remains incomplete.`;
        } catch (error) {
          console.warn('AVID Titanium Center could not load its complete hardware index.', error);
          catalogLoadError = 'The full hardware index could not be loaded. Browse the visible products or ask AVID to verify an exact specification.';
          catalogComplete = false;
        }
        populateFacets();
        applyUrl();
        populateFacets();
        render();
      })();
      try {
        return await catalogLoadPromise;
      } finally {
        catalogLoadPromise = null;
      }
    };

    const requestCompleteCatalogForExactFilters = () => {
      if (hasExactFilter(readFilters())) void completeCatalog();
    };

    form.addEventListener('input', () => {
      requestCompleteCatalogForExactFilters();
      queueRender();
    });
    form.addEventListener('change', () => {
      requestCompleteCatalogForExactFilters();
      queueRender();
    });
    sort?.addEventListener('change', render);
    form.addEventListener('reset', () => window.setTimeout(() => {
      visibleCount = pageSize;
      facetWidgets.forEach((widget) => widget.sync());
      render();
    }, 0));
    showMore?.addEventListener('click', () => {
      visibleCount += pageSize;
      render();
    });
    retryCatalog?.addEventListener('click', async () => {
      retryCatalog.disabled = true;
      try {
        await completeCatalog({ forceReload: loadedPages.size >= totalPages });
      } finally {
        retryCatalog.disabled = false;
      }
    });

    if (requestedExactFilter) {
      await completeCatalog();
    } else {
      populateFacets();
      applyUrl();
      populateFacets();
      render();
      if (!catalogComplete) void completeCatalog();
    }
  };

  const init = () => document.querySelectorAll('[data-titanium-center]').forEach(initTitaniumCenter);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
